import { baseDataZoom, EChartsOption, TNumberValue } from 'volta/components/bv-chart';
import { BufferIconBase64, extractLabels, mapSeries } from 'volta/utils/chart-utils';
import { getColor, SemanticColors } from 'volta/utils/style-utils';

import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import type IntlService from 'ember-intl/services/intl';
import type ExecutionAlertModel from 'volta/models/execution-alert';
import type { IExecutionAlertTs, IExecutionProjectionTsItem } from 'volta/models/types/planning';
import type AppNavState from 'volta/services/app-nav-state';

type TChart = 'buffer' | 'avgOnHand' | 'realBalance' | 'simulatedBalance';

type TSeriesKey =
  | 'bufferRedBase'
  | 'bufferRedSafety'
  | 'bufferYellow'
  | 'bufferGreen'
  | 'avgOnHand'
  | 'realBalance'
  | 'simulatedBalance'
  | 'bufferTOR'
  | 'bufferTOY'
  | 'bufferTOG';

type TKeys = keyof IExecutionProjectionTsItem;

const SeriesDefinitions = (isBuffered: boolean = true, theme: 'light' | 'dark') => {
  const result: TSeriesDefinitions<TChart, TSeriesKey> = {};

  if (isBuffered) {
    result['buffer'] = {
      withArea: true,
      legendIcon: BufferIconBase64,
      legendLabel: 'buffer',
      series: {
        bufferRedBase: {
          label: 'redBase',
          color: getColor('critical'),
          excludeFromTooltip: true
        },
        bufferRedSafety: {
          label: 'redSafety',
          color: getColor('danger'),
          tooltipOverride: { data: 'bufferTOR', label: 'TOR' }
        },

        bufferYellow: {
          label: 'yellow',
          color: getColor('warning'),
          tooltipOverride: { data: 'bufferTOY', label: 'TOY' }
        },

        bufferGreen: {
          label: 'green',
          color: getColor('success'),
          tooltipOverride: { data: 'bufferTOG', label: 'TOG' }
        }
      }
    };
  }
  return {
    ...result,
    avgOnHand: {
      hidden: true,
      series: {
        avgOnHand: {
          label: 'avgTargetOnHand',
          color: SemanticColors.bvText(theme),
          dashed: true
        }
      }
    },
    realBalance: {
      series: {
        realBalance: {
          label: 'realBalance',
          color: getColor('secondary'),
          dashed: false
        }
      }
    },
    simulatedBalance: {
      legendLabel: 'simulatedBalance',
      series: {
        simulatedBalance: {
          label: 'simulatedBalance',
          color: getColor('primary'),
          dashed: true
        }
      }
    }
  } as TSeriesDefinitions<TChart, TSeriesKey>;
};

let bufferKeys = [
  'bufferRedBase',
  'bufferRedSafety',
  'bufferYellow',
  'bufferGreen',
  'bufferTOG',
  'bufferTOY',
  'bufferTOR'
];

interface IArgs {
  ts?: IExecutionAlertTs;
  executionAlert?: ExecutionAlertModel;
  projection?: IExecutionProjectionTsItem[];
  loading: boolean;
}

export default class ExecutionChart extends Component<IArgs> {
  @service intl!: IntlService;
  @service appNavState!: AppNavState;

  @cached
  get defs() {
    return SeriesDefinitions(
      this.args.executionAlert?.sku.isBuffered,
      this.appNavState.appliedTheme
    );
  }

  @cached
  get labels() {
    const { defs, intl } = this;
    return extractLabels(defs, intl);
  }

  @cached
  get data() {
    const { executionAlert, projection, ts } = this.args;
    const { labels } = this;

    const projectionArray = projection?.length ? projection.toArray() : [];

    if (!projectionArray.length || !ts || !executionAlert) {
      return;
    }

    const data: Partial<Record<TKeys, TNumberValue[]>> = {
      realBalance: [] as TNumberValue[],
      simulatedBalance: [] as TNumberValue[],
      bufferRedBase: [] as TNumberValue[],
      bufferRedSafety: [] as TNumberValue[],
      bufferYellow: [] as TNumberValue[],
      bufferGreen: [] as TNumberValue[],
      bufferTOG: [] as TNumberValue[],
      bufferTOY: [] as TNumberValue[],
      bufferTOR: [] as TNumberValue[]
    };

    const dueDates: string[] = [];
    const avgOnHand = [] as TNumberValue[];

    const keys = Object.keys(data);

    // Fill past buffers time series
    // ~~~~
    const historyDates = ts?.inventoryDate ?? [];

    historyDates.forEach((date, i) => {
      dueDates.push(date);
      data.realBalance?.push(ts['onHand'][i] ?? '-');
      data.simulatedBalance?.push('-');
      avgOnHand.push(ts['avgTargetOnHand'][i] ?? '-');

      bufferKeys.forEach((k: keyof IExecutionAlertTs) => {
        const value = (ts[k][i] ?? '-') as TNumberValue;
        data[k as TKeys]?.push(value);
      });
    });

    projectionArray.forEach((item) => {
      const { dueDate } = item;

      dueDates.push(dueDate);
      avgOnHand.push(item.bufferTOR + item.bufferGreen * 0.5);

      keys.forEach((k: TKeys) => {
        data[k]?.push(item[k] as number);
      });
    });
    const result = { ...data, avgOnHand, dueDate: dueDates };
    return Object.entries(result).reduce((acc: Record<string, any>, [key, ts]) => {
      acc[labels[key] ?? key] = ts;
      return acc;
    }, {});
  }

  get chartOptions(): EChartsOption {
    const { defs, data, labels } = this;

    if (!data) {
      return {};
    }

    const series: EChartsOption[] = [];
    const today = new Date().toISOString();

    const legendData: { selected: Record<string, boolean>; data: object[] } = {
      selected: {},
      data: []
    };

    mapSeries(defs, (chartKey, chartOpt, seriesKey, seriesOpt, _index) => {
      const chartLabel = this.intl.t(chartOpt.legendLabel ?? chartKey);
      const areaStyle = chartOpt.withArea ? { color: seriesOpt.color } : undefined;
      const name = labels[seriesKey];
      legendData.selected[name] = !chartOpt?.hidden;
      legendData.data.push({
        name: chartOpt.withArea ? chartLabel : name,
        icon: chartOpt.withArea ? BufferIconBase64 : undefined
      });

      series.push({
        id: seriesKey,
        name: chartOpt.withArea ? chartLabel : name,
        type: 'line',
        showSymbol: false,
        color: seriesOpt.color,
        step: chartOpt.withArea && 'middle',
        stack: chartOpt.withArea && chartLabel,
        tooltip: {
          show: !seriesOpt.excludeFromTooltip
        },
        encode: {
          x: 'dueDate',
          y: name,
          tooltip: seriesOpt.tooltipOverride
            ? (seriesKey === 'bufferRedSafety' ? [labels['bufferRedBase']] : []).concat([
                name,
                labels[seriesOpt.tooltipOverride.data]
              ])
            : undefined
        },
        lineStyle: {
          type: seriesOpt.dashed ? [3, 3] : undefined,
          width: 2
        },
        areaStyle
      });
    });

    series.push({
      name: 'today',
      type: 'line',
      tooltip: { show: false },
      markLine: {
        symbol: 'none',
        label: { formatter: () => this.intl.t('today') },
        data: [{ xAxis: today }, { xAxis: today }]
      }
    });

    return {
      dataset: {
        dimensions: ['dueDate'].concat(Object.values(labels)),
        source: data
      },
      legend: {
        selected: legendData.selected,
        data: legendData.data
      },
      xAxis: {},
      tooltip: {},
      dataZoom: baseDataZoom(),
      series
    } as EChartsOption;
  }
}
